import { useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, TextField, Typography } from '@mui/material';
import { useDebounce } from '../../shared/hooks';
import { useField } from '@unform/core';
import { ParceirosService } from '../services/api/Venda/ParceiroService';

type TAutoCompleteOption = {
  idparceiro: string;
  label: string;
  cfop: number;
  diasvencimento: number;
  cpf: string;
  // Linha sintética adicionada só pro rodapé "todos os N itens já foram
  // carregados" (mesmo padrão de AutoCompleteProdutopdv.tsx) — nunca vem do
  // backend, nunca é selecionável.
  _footer?: boolean;
}

// Quantos parceiros buscar por página. Ao chegar no último item carregado e
// apertar seta pra baixo (ver handleHighlightChange), busca a próxima leva
// com o mesmo tamanho — mesmo comportamento de AutoCompleteProdutopdv.tsx.
const TAMANHO_PAGINA = 8;

const FOOTER_TODOS_CARREGADOS: TAutoCompleteOption = {
  idparceiro: '__footer_todos_carregados__',
  label: '',
  cfop: 0,
  diasvencimento: 0,
  cpf: '',
  _footer: true,
};

interface IAutoCompleteParceiroProps {
  isExternalLoading?: boolean;
  uuidparceiro?: (novoTextoselect: string | undefined) => void;
  cfops?: (novoTextoselect: number | undefined) => void;
  diasvencimento?: (novoTextoselect: number | undefined) => void;
  cpfs?: (novoTextoselect: string | undefined) => void;
  inputRef?: React.Ref<HTMLInputElement>;
}

export const AutoCompleteParceiro: React.FC<IAutoCompleteParceiroProps> = ({ isExternalLoading = false, uuidparceiro, diasvencimento, cfops, cpfs, inputRef}) => {

  const { fieldName, registerField, defaultValue, error, clearError } = useField('idparceiro');

  const { debounce } = useDebounce();

  const [selectedId, setSelectedId] = useState<string | undefined>(defaultValue);

  const [opcoes, setOpcoes] = useState<TAutoCompleteOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [busca, setBusca] = useState('');
  // Página já carregada e total de registros que a busca atual tem no
  // servidor — junto dão pra saber se ainda há mais itens pra buscar quando
  // o operador chegar no fim da lista carregada.
  const [pagina, setPagina] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const requestIdRef = useRef(0);
  const carregandoMaisRef = useRef(false);
  // Última seta pressionada — só busca a próxima página quando o destaque
  // chega no último item DESCENDO (seta pra baixo), mesmo motivo de
  // AutoCompleteProdutopdv.tsx.
  const ultimaSetaRef = useRef<'up' | 'down' | null>(null);

  useEffect(() => {
  if (defaultValue !== undefined) {
    setSelectedId(defaultValue);
  }
}, [defaultValue]);
  useEffect(() => {
    registerField({
      name: fieldName,
      getValue: () => selectedId,
      setValue: (_, newSelectedId) => setSelectedId(newSelectedId),
    });
  }, [registerField, fieldName, selectedId]);

  const mapParceiro = (parceiro: { idparceiro: string; nome: string; cpf: string; cfop: number; diasvencimento: number }): TAutoCompleteOption => ({
    idparceiro: parceiro.idparceiro,
    label: parceiro.nome + ' - ' + parceiro.cpf,
    cfop: parceiro.cfop,
    diasvencimento: parceiro.diasvencimento,
    cpf: parceiro.cpf,
  });

  useEffect(() => {
    setIsLoading(true);
    const requestId = ++requestIdRef.current;

    debounce(() => {
      ParceirosService.getAllList(busca, selectedId, 1, TAMANHO_PAGINA)
        .then((result) => {
          if (requestId !== requestIdRef.current) return; // resposta antiga, ignora
          setIsLoading(false);
          if (result instanceof Error) {
            // alert(result.message);
          } else {
            setOpcoes(result.data.map(mapParceiro));
            setTotalCount(result.totalCount);
            setPagina(1);
          }
        });
    });
  }, [busca, selectedId, debounce]);

  // Busca a próxima leva de resultados e ANEXA ao final da lista já
  // carregada — chamado ao navegar com a seta pra baixo e chegar no último
  // item carregado (ver handleHighlightChange abaixo).
  const carregarProximaPagina = () => {
    if (!busca?.trim() || carregandoMaisRef.current || opcoes.length >= totalCount) return;

    carregandoMaisRef.current = true;
    const requestId = requestIdRef.current;
    const proximaPagina = pagina + 1;

    ParceirosService.getAllList(busca, '', proximaPagina, TAMANHO_PAGINA)
      .then((result) => {
        carregandoMaisRef.current = false;
        if (requestId !== requestIdRef.current) return; // busca mudou nesse meio tempo
        if (result instanceof Error) return;

        setOpcoes((prev) => [...prev, ...result.data.map(mapParceiro)]);
        setPagina(proximaPagina);
      })
      .catch(() => {
        carregandoMaisRef.current = false;
      });
  };

  const handleHighlightChange = (
    _event: React.SyntheticEvent,
    option: TAutoCompleteOption | null,
    reason: string,
  ) => {
    if (reason !== 'keyboard' || !option || ultimaSetaRef.current !== 'down') return;
    const indiceAtual = opcoes.findIndex((opcao) => opcao.idparceiro === option.idparceiro);
    if (indiceAtual === opcoes.length - 1) {
      carregarProximaPagina();
    }
  };

  const autoCompleteSelectedOption = useMemo(() => {
    if (!selectedId) return null;

    const selectedOption = opcoes.find(opcao => opcao.idparceiro === selectedId);
    if (!selectedOption) return null;

    return selectedOption;
  }, [selectedId, opcoes]);

  // Some ao fim da lista só depois de carregar tudo que a busca tem — mesmo
  // padrão de AutoCompleteProdutopdv.tsx (linha de rodapé navegável, não
  // desabilitada, pra não disparar o wrap incondicional do MUI).
  const opcoesExibidas = opcoes.length > 0 && opcoes.length >= totalCount
    ? [...opcoes, FOOTER_TODOS_CARREGADOS]
    : opcoes;

  return (
    <Autocomplete
      openText='Abrir'
      closeText='Fechar'
      noOptionsText='Sem opções'
      loadingText='Carregando...'
      disablePortal
      disableListWrap
      options={opcoesExibidas}
      loading={isLoading}
      disabled={isExternalLoading}
      value={autoCompleteSelectedOption}
      onInputChange={(_, newValue) => setBusca(newValue)}
      onHighlightChange={handleHighlightChange}
      onChange={(_, newValue) => {
        if (newValue?._footer) return;
        setSelectedId(newValue?.idparceiro); uuidparceiro?.(newValue?.idparceiro); cpfs?.(newValue?.cpf); cfops?.(newValue?.cfop);
        diasvencimento?.(newValue?.diasvencimento); setBusca(''); clearError();
      }}
      renderOption={(props, option) => (
        option._footer ? (
          <li {...props} key={option.idparceiro} style={{ pointerEvents: 'none' }}>
            <Typography
              variant="caption"
              align="center"
              sx={{ width: '100%', opacity: 0.6, fontStyle: 'italic', py: 0.25 }}
            >
              Todos os {totalCount} parceiro{totalCount === 1 ? '' : 's'} encontrados foram exibidos
            </Typography>
          </li>
        ) : (
          <li {...props} key={option.idparceiro}>{option.label}</li>
        )
      )}
      popupIcon={(isLoading) ? <CircularProgress size={28} /> : undefined}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          size='small'
          label="Parceiros"
          error={!!error}
          helperText={error}
          onKeyDown={(event) => {
            if (event.key === 'ArrowUp') { ultimaSetaRef.current = 'up'; return; }
            if (event.key === 'ArrowDown') { ultimaSetaRef.current = 'down'; }
          }}
        />
      )}
    />
  );
};
